import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, HelpCircle, Phone, ArrowRight, X } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { fetchFaqs, fetchFaqCategories } from "@/lib/api";
import LoadingState from "@/components/LoadingState";
import { CONTACT } from "@/lib/constants";

export default function FAQ() {
    const [faqs, setFaqs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState("All");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchFaqs(), fetchFaqCategories()])
            .then(([f, c]) => { setFaqs(f); setCategories(c.categories || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let list = faqs;
        if (category !== "All") list = list.filter(f => f.category === category);
        if (q.trim()) {
            const s = q.trim().toLowerCase();
            list = list.filter(f =>
                f.question.toLowerCase().includes(s) ||
                f.answer.toLowerCase().includes(s) ||
                (f.tags || []).some(t => t.toLowerCase().includes(s))
            );
        }
        return list;
    }, [faqs, category, q]);

    const grouped = useMemo(() => {
        const g = {};
        for (const f of filtered) {
            (g[f.category] ||= []).push(f);
        }
        return g;
    }, [filtered]);

    const totalCount = faqs.length;
    const categoryList = [{ name: "All", count: totalCount }, ...categories];

    // JSON-LD FAQPage schema for SEO
    const jsonLd = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.slice(0, 30).map(f => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": { "@type": "Answer", "text": f.answer }
        }))
    }), [faqs]);

    return (
        <div data-testid="faq-page">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />

            {/* Header */}
            <section className="container-tight pt-16 md:pt-24 pb-10 border-b border-zinc-800">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="eyebrow">Help Center</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mt-3 leading-[1.02]">
                            Printer problems, <span className="text-zinc-500">answered.</span>
                        </h1>
                        <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
                            Paper jams, faded prints, stubborn Wi-Fi, and 3D printing gotchas — the fixes that actually work,
                            written by people who&apos;ve spent years unjamming, unclogging, and re-leveling.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="card-flat p-5 md:p-6">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-sm bg-blue-600/15 border border-blue-600/30 flex items-center justify-center flex-shrink-0">
                                    <Phone className="h-4 w-4 text-blue-400" />
                                </div>
                                <div>
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Still stuck?</div>
                                    <a href={CONTACT.phoneHref} data-testid="faq-phone-cta" className="font-display font-semibold text-white hover:text-blue-400 transition-colors">
                                        Call {CONTACT.phoneDisplay}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Search + filters */}
            <section className="container-tight py-10 sticky top-16 md:top-20 z-30 bg-zinc-950/85 backdrop-blur-xl border-b border-zinc-800 -mt-px">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search FAQs — try 'paper jam', 'wifi', 'first layer'…"
                            data-testid="faq-search"
                            className="bg-zinc-950 border-zinc-800 pl-9 pr-9 rounded-sm h-11 focus-visible:ring-blue-500"
                        />
                        {q && (
                            <button
                                onClick={() => setQ("")}
                                aria-label="Clear search"
                                data-testid="faq-search-clear"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center text-zinc-500 hover:text-white transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    <div className="flex-shrink-0 flex flex-wrap gap-2" data-testid="faq-category-tabs">
                        {categoryList.map(c => {
                            const slug = c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-+|-+$/g, "");
                            return (
                                <button
                                    key={c.name}
                                    onClick={() => setCategory(c.name)}
                                    data-testid={`faq-cat-${slug}`}
                                    className={`px-3.5 py-2 text-xs md:text-sm font-medium rounded-sm border transition-colors duration-200 ${
                                        category === c.name
                                            ? "bg-blue-600 text-white border-blue-600"
                                            : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-zinc-600 hover:text-white"
                                    }`}
                                >
                                    {c.name} <span className="ml-1 font-mono text-[11px] opacity-60">{c.count}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ list */}
            <section className="container-tight py-12 md:py-16">
                {loading ? (
                    <LoadingState label="Loading FAQs" />
                ) : filtered.length === 0 ? (
                    <div className="card-flat p-12 text-center" data-testid="faq-empty">
                        <HelpCircle className="h-8 w-8 text-zinc-600 mx-auto" />
                        <div className="text-zinc-300 mt-4 font-display text-lg">No results for &quot;{q}&quot;</div>
                        <p className="text-zinc-500 text-sm mt-2">Try a different search or clear the filters.</p>
                        <button onClick={() => { setQ(""); setCategory("All"); }} className="btn-ghost mt-6">Reset</button>
                    </div>
                ) : (
                    <div className="space-y-14" data-testid="faq-groups">
                        {Object.entries(grouped).map(([cat, items]) => {
                            const slug = cat.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-+|-+$/g, "");
                            return (
                                <div key={cat} data-testid={`faq-group-${slug}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-white">{cat}</h2>
                                    <span className="text-[11px] tracking-[0.22em] uppercase text-zinc-500 font-mono">{items.length} item{items.length > 1 ? "s" : ""}</span>
                                </div>
                                <Accordion type="multiple" className="border-t border-zinc-800">
                                    {items.map((f) => (
                                        <AccordionItem
                                            key={f.id}
                                            value={f.id}
                                            data-testid={`faq-item-${f.id}`}
                                            className="border-b border-zinc-800"
                                        >
                                            <AccordionTrigger className="text-base md:text-lg py-6 hover:no-underline text-white font-display font-medium tracking-tight [&>svg]:text-zinc-500 [&[data-state=open]>svg]:text-blue-400">
                                                <span className="pr-8">{f.question}</span>
                                            </AccordionTrigger>
                                            <AccordionContent className="pb-6 pt-0">
                                                <div className="text-zinc-300 leading-relaxed text-base whitespace-pre-line">{f.answer}</div>
                                                {f.tags?.length > 0 && (
                                                    <div className="mt-4 flex flex-wrap gap-1.5">
                                                        {f.tags.map(t => (
                                                            <button
                                                                key={t}
                                                                onClick={() => setQ(t)}
                                                                className="text-[10px] tracking-widest uppercase px-2 py-1 border border-zinc-800 text-zinc-500 hover:text-blue-400 hover:border-blue-500/50 rounded-sm transition-colors"
                                                            >
                                                                #{t}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Bottom CTA */}
            <section className="border-t border-zinc-800 bg-zinc-950/60" data-testid="faq-bottom-cta">
                <div className="container-tight py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="eyebrow">Not seeing your issue?</div>
                        <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mt-3">
                            Talk to a human. We&apos;ve seen it before.
                        </h3>
                        <p className="text-zinc-400 mt-3 leading-relaxed max-w-md">
                            Describe your problem — model, symptom, what you&apos;ve tried — and we&apos;ll suggest the next step.
                            No sales pitch, just a quick reply.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 md:justify-end">
                        <a href={CONTACT.phoneHref} className="btn-primary" data-testid="faq-cta-call">
                            <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                        </a>
                        <Link to="/contact" className="btn-ghost" data-testid="faq-cta-contact">
                            Send a message <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
